import React, { useEffect } from 'react';
import { Container, Typography, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { CheckCircle, Mic, Zap, ShieldCheck, Code, MessageSquare, Repeat } from 'lucide-react';
import Header from '../header/header';
import Footer from '../footer/Footer';

const GuideToFeatures = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="App-main">
            <Header />
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, borderRadius: 4 }}>
                    <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800, color: '#1a202c' }}>
                        The Ultimate Guide to ConvoCue AI Features
                    </Typography>
                    
                    <Typography variant="subtitle1" sx={{ mb: 4, color: '#4a5568', fontSize: '1.2rem', textAlign: 'left' }}>
                        Mastering job interviews requires more than just memorization. ConvoCue AI is your intelligent interview copilot, providing real-time assistance and technical support to ensure you land your dream role.
                    </Typography>

                    <Box sx={{ mb: 6, textAlign: 'left' }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                            Core Strengths of ConvoCue AI
                        </Typography>
                        <Typography variant="body1" paragraph>
                            ConvoCue stands out by offering a seamless blend of behavioral guidance and technical expertise, all delivered through an interface that understands the flow of a real conversation.
                        </Typography>
                        <List>
                            {[
                                { text: 'Context-Aware Conversation Hints', icon: <MessageSquare size={20} /> },
                                { text: 'Support for Follow-up Questions', icon: <Repeat size={20} /> },
                                { text: 'Real-time Live Coding Copilot', icon: <Code size={20} /> },
                                { text: 'Industry-Specific Mock Simulations', icon: <ShieldCheck size={20} /> }
                            ].map((item, index) => (
                                <ListItem key={index}>
                                    <ListItemIcon sx={{ color: '#3b82f6' }}>{item.icon}</ListItemIcon>
                                    <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: 600 }} />
                                </ListItem>
                            ))}
                        </List>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box sx={{ mb: 6, textAlign: 'left' }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                            1. Context-Aware Intelligence
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Unlike generic AI tools, ConvoCue maintains the full context of your interview. It doesn't just treat every question in isolation; it understands the "thread" of your discussion.
                        </Typography>
                        <Box sx={{ bgcolor: '#eff6ff', p: 3, borderRadius: 2, mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e40af', mb: 1 }}>Smarter Hints:</Typography>
                            <Typography variant="body2" component="div">
                                <ul>
                                    <li><strong>Discussion History:</strong> ConvoCue provides hints based on what you've already discussed, ensuring your answers remain consistent and build upon previous points.</li>
                                    <li><strong>Follow-up Ready:</strong> When an interviewer asks "Can you dive deeper into that?", ConvoCue is already prepared with the relevant details from your background and the current context.</li>
                                </ul>
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 6, textAlign: 'left' }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                            2. Live Coding Copilot
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Technical interviews often hinge on live coding sessions. ConvoCue's built-in coding assistant ensures you never get stuck on syntax or logic gaps again.
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemIcon><CheckCircle color="#10b981" size={20} /></ListItemIcon>
                                <ListItemText primary="Instant Solutions: Get immediate answers to coding problems without needing to search external sites." />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircle color="#10b981" size={20} /></ListItemIcon>
                                <ListItemText primary="Algorithm Optimization: Receive suggestions on how to improve the time and space complexity of your code." />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircle color="#10b981" size={20} /></ListItemIcon>
                                <ListItemText primary="Language Support: Expertise across Python, Java, C++, JavaScript, and more." />
                            </ListItem>
                        </List>
                    </Box>

                    <Box sx={{ mb: 6, textAlign: 'left' }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                            3. Realistic Interview Simulations
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Prepare with the most accurate industry-specific scenarios available. Our AI is trained on diverse datasets to provide relevant assistance across:
                        </Typography>
                        <Typography variant="body1" component="div">
                            <ul>
                                <li><strong>Software Engineering & SDE:</strong> System design, data structures, and algorithms.</li>
                                <li><strong>Data Science & ML:</strong> Model evaluation, statistics, and business case studies.</li>
                                <li><strong>Product Management:</strong> Product sense, execution, and strategy questions.</li>
                                <li><strong>Financial Engineering:</strong> Quantitative analysis and market logic.</li>
                            </ul>
                        </Typography>
                    </Box>

                    <Box sx={{ textAlign: 'center', mt: 8, p: 4, bgcolor: '#1a202c', color: 'white', borderRadius: 4 }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                            Experience the Future of Interview Prep
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                            Stop searching for answers and start practicing with a copilot that understands you.
                        </Typography>
                        <Box component="a" href="/" sx={{ 
                            display: 'inline-block', 
                            bgcolor: '#3b82f6', 
                            color: 'white', 
                            px: 4, 
                            py: 2, 
                            borderRadius: 2, 
                            textDecoration: 'none',
                            fontWeight: 700,
                            '&:hover': { bgcolor: '#2563eb' }
                        }}>
                            Get Started Now
                        </Box>
                    </Box>
                </Paper>
            </Container>
            <Footer theme="light" />
        </div>
    );
};

export default GuideToFeatures;
